import { useEffect, useState } from 'react';

import { ApiError } from '../../lib/api.js';
import { useCan } from '../../lib/permissions.js';
import { Callout } from '../Callout.js';
import { QueryState } from '../QueryState.js';
import { CreditPackagePicker } from './CreditPackagePicker.js';
import { CreditTransactionHistory } from './CreditTransactionHistory.js';
import { creditAmount } from './format.js';
import { usePendingCreditPurchase } from './sessionMemory.js';
import { useProjectCredits } from './useProjectCredits.js';

import '../../styles/credits.css';

/**
 * PROJECT CREDITS ON PLAN & BILLING (S4, design B.8).
 *
 * What the production can spend on AI, first and largest; what is set aside for
 * operations still running, when anything is; what was bought, what was given
 * and what has been spent. Every figure is the server's, printed as it came
 * (the balance floored to the cent, so it never shows more than is spendable).
 *
 * Who sees what (B.6, B.7):
 *   - every member sees the figures and the history;
 *   - only a member who may buy (`BILLING_PURCHASE`) is offered the packages,
 *     and a refusal from the server takes the offer away too;
 *   - a read-only production is offered the renewal instead, a paused one
 *     nothing at all.
 *
 * BEFORE LAUNCH (`LEGACY`) THE PANEL IS NOT THERE: today's Plan & billing,
 * exactly.
 */
export function ProjectCreditsPanel({ projectId, readOnly = false }: { projectId: string; readOnly?: boolean }) {
  const credits = useProjectCredits(projectId);
  const mayBuy = useCan('BILLING_PURCHASE');
  const [buying, setBuying] = useState(false);
  const [refused, setRefused] = useState(false);
  // A purchase this tab was confirming (a reload, a redirect's return) opens the picker on its outcome.
  const pending = usePendingCreditPurchase(projectId);

  useEffect(() => {
    if (pending !== null) setBuying(true);
  }, [pending]);

  const wallet = credits.data;

  if (wallet?.aiBilling === 'LEGACY') return null;
  // No wallet for this production (an API from before credits, or a wallet not
  // created yet) is the pre-launch state, not a failure to report on every visit.
  if (credits.error instanceof ApiError && credits.error.status === 404) return null;

  const suspended = wallet?.aiBilling === 'SUSPENDED';
  const offerPurchase = mayBuy && !refused && !readOnly && !suspended;
  const picking = buying && offerPurchase;

  return (
    <section className="credits-panel card1" data-testid="project-credits-panel" aria-labelledby="project-credits-title">
      <div className="credits-panel-head">
        <h3 className="dh" id="project-credits-title" style={{ fontSize: 19, margin: 0 }}>
          Project credits
        </h3>
        {offerPurchase && !picking && wallet !== undefined ? (
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="project-credits-buy"
            onClick={() => {
              setBuying(true);
            }}
          >
            Buy credits
          </button>
        ) : null}
      </div>
      <p className="mm credits-note" style={{ marginTop: 6 }}>
        Spent on this production’s AI work. You see the cost of each operation before it starts, and only
        what it uses is charged.
      </p>

      <QueryState query={credits} subject="the project credits" testId="project-credits" />

      {wallet !== undefined ? (
        <>
          <div className="credits-balance" data-testid="project-credits-available">
            <span className="credits-balance-label">Available project credits</span>
            <span className="credits-balance-value" data-testid="project-credits-available-value">
              {creditAmount(wallet.availableCents)}
            </span>
          </div>

          <dl className="credits-totals" data-testid="project-credits-totals">
            {wallet.reservedCents === 0 ? null : (
              <div data-testid="project-credits-reserved">
                <dt>Set aside for running AI</dt>
                <dd>{creditAmount(wallet.reservedCents)}</dd>
              </div>
            )}
            {wallet.frozenCents === 0 ? null : (
              <div data-testid="project-credits-frozen">
                <dt>On hold during a card dispute</dt>
                <dd>{creditAmount(wallet.frozenCents)}</dd>
              </div>
            )}
            <div data-testid="project-credits-purchased">
              <dt>Purchased</dt>
              <dd>{creditAmount(wallet.purchasedCents)}</dd>
            </div>
            <div data-testid="project-credits-bonus">
              <dt>Bonus credits</dt>
              <dd>{creditAmount(wallet.bonusCents)}</dd>
            </div>
            <div data-testid="project-credits-consumed">
              <dt>Spent on AI</dt>
              <dd>{creditAmount(wallet.consumedCents)}</dd>
            </div>
          </dl>

          {suspended ? (
            <div style={{ marginTop: 14 }}>
              <Callout tone="warn" title="AI analysis is paused" testId="project-credits-paused">
                New AI work cannot be started and credits cannot be bought right now. Anything already
                running finishes and is charged as usual, and these credits are safe.
              </Callout>
            </div>
          ) : readOnly ? (
            <div style={{ marginTop: 14 }}>
              <Callout tone="warn" title="Renew the plan to add credits" testId="project-credits-read-only">
                This production is read-only, so credits cannot be added and AI cannot run. Choose a plan
                below to start again; the credits above are kept.
              </Callout>
            </div>
          ) : !mayBuy || refused ? (
            <p className="mm credits-note" data-testid="project-credits-ask-producer">
              A producer can add credits to this production.
            </p>
          ) : null}

          {picking ? (
            <CreditPackagePicker
              projectId={projectId}
              onClose={() => {
                setBuying(false);
              }}
              onForbidden={() => {
                setRefused(true);
                setBuying(false);
              }}
            />
          ) : null}

          <CreditTransactionHistory projectId={projectId} />
        </>
      ) : null}
    </section>
  );
}
